<template>
  <v-container class="pa-4">
    <div class="pt-4 pb-6">
      <v-btn text ref="button-toolbar-close" @click="$emit('close')">
        <v-icon>close</v-icon>
        <span>{{ $t('general.close') }}</span>
      </v-btn>
    </div>
    <v-card class="rounded-lg">
      <v-row>
        <v-col
          cols="12"
          md="8"
          class="d-flex flex-column justify-center align-center"
        >
          <v-card-text class="title">
            {{ $t('organization_billing.billing_history.subtitle') }}
          </v-card-text>
          <v-card-text style="color: #2661FF;">
            {{ $t('organization_billing.billing_history.description') }}
          </v-card-text>
        </v-col>
        <v-col cols="12" md="4" class="d-flex justify-center align-center">
          <!-- HAS TO CREATE A DIALOG FOR EXPORT BECAUSE IT RECALL THE EXPORT OF Organization.vue-->
          <v-dialog v-model="exportDialog" max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                ref="button-open-export-dialog"
                color="#2661ff"
                class="mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left style="color: #FFF;">cloud_download</v-icon>
                <span style="color: #FFF;">{{ $t('general.export') }}</span>
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="px-8 py-4">
                <v-row dense>
                  <v-col cols="12" class="d-flex justify-center">
                    <span class="primary--text subtitle-2 font-weight-medium">
                      <b>
                        {{ $t('downloads.dialog1.title') }}
                      </b>
                    </span>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text class="px-8 pt-4 pb-0">
                <v-row dense>
                  <v-col cols="12">
                    <v-select
                      :items="fileTypes"
                      v-model="fileType"
                      :label="$t('downloads.dialog1.file_type')"
                      v-validate="'required'"
                      :data-vv-name="$t('downloads.dialog1.file_type')"
                      :error-messages="
                        errors.collect($t('downloads.dialog1.file_type'))
                      "
                      item-text="name"
                      item-value="value"
                      dense
                      outlined
                      required
                    />
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions class="px-8 pt-0 pb-4">
                <v-row dense>
                  <v-col cols="6" class="d-flex justify-end">
                    <v-btn
                      @click="exportFile()"
                      :loading="exportLoading"
                      color="primary"
                      small
                      block
                    >
                      {{ $t('downloads.dialog1.button_export') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="d-flex justify-start">
                    <v-btn
                      @click="close()"
                      :disabled="exportLoading"
                      color="primary"
                      outlined
                      small
                      block
                    >
                      {{ $t('general.cancel') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :loading="loading"
            :items="billingHistoryTable.items"
            :headers="billingHistoryTable.headers"
            :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
            :items-per-page="billingHistoryTable.pagination.itemsPerPage"
            :server-items-length="billingHistoryTable.total"
            :no-data-text="$t('subscriptions_report.no_data')"
            :no-results-text="$t('subscriptions_report.no_data')"
            @update:options="billingHistoryTable.pagination = $event"
            @click:row="openBillingDetails"
          >
            <!-- SHOWS NO DATA -->
            <template v-slot:no-data>
              <div class="text-center pa-5 my-5">
                <v-img
                  :src="require('@/assets/svg/box_empty.svg')"
                  height="150"
                  max-height="150px"
                  aspect-ratio="1"
                  contain
                />
                <div class="grey--text">
                  {{ $t('subscriptions_report.no_data') }}
                </div>
              </div>
            </template>

            <template v-slot:item.billing_date_month="{ item }">
              {{ item.billing_date | moment('MM') }}
            </template>
            <template v-slot:item.billing_date="{ item }">
              {{ item.billing_date | moment('YYYY') }}
            </template>
            <template v-slot:item.plan.name="{ item }">
              {{ item.plan.name }}
            </template>
            <template v-slot:item.currency.name="{ item }">
              {{ item.currency.name }}
            </template>
            <template v-slot:item.incomes_amount="{ item }">
              {{
                item.incomes_amount | valueFormatCurrency(item.currency.symbol)
              }}
            </template>
            <template v-slot:item.fee_percentage="{ item }">
              {{ item.fee_percentage ? `${item.fee_percentage} %` : '-' }}
            </template>
            <template v-slot:item.collection_fee="{ item }">
              {{
                item.collection_fee | valueFormatCurrency(item.currency.symbol)
              }}
            </template>
            <template v-slot:item.amount="{ item }">
              {{ item.amount | valueFormatCurrency(item.currency.symbol) }}
            </template>
            <template v-slot:item.services_amount="{ item }">
              {{
                item.services_amount | valueFormatCurrency(item.currency.symbol)
              }}
            </template>
            <template v-slot:item.total="{ item }">
              {{ item.total | valueFormatCurrency(item.currency.symbol) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon @click="removeBilling(item.id)">
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <BillingDetail v-if="isBillingDetailOpen" />
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import DialogExport from '@/components/Downloads/DialogExport'
import DialogMessage from '@/components/Downloads/DialogMessage'
import BillingDetail from './BillingDetail.vue'

export default {
  name: 'OrganizationBillingHistory',
  components: {
    DialogExport,
    DialogMessage,
    BillingDetail
  },
  props: {
    organization_id: { type: Number, required: true }
  },
  data() {
    return {
      loading: false,
      exportLoading: false,
      exportDialog: false,
      billingHistoryTable: {
        items: [],
        total: 0,
        pagination: {
          sortBy: ['billing_date'],
          sortDesc: [false],
          itemsPerPage: 10
        },
        headers: [
          {
            text: this.$t('organization_billing.billing_history.month'),
            sortable: true,
            value: 'billing_date_month',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization_billing.billing_history.year'),
            sortable: true,
            value: 'billing_date',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Nombre del plan',
            sortable: false,
            value: 'plan.name',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Moneda del plan',
            sortable: false,
            value: 'currency.name',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Ingresos (Mes)',
            sortable: false,
            value: 'incomes_amount',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Porcentaje %',
            sortable: false,
            value: 'fee_percentage',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Collection Fee',
            sortable: false,
            value: 'collection_fee',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Monto',
            sortable: false,
            value: 'amount',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Services cost',
            sortable: false,
            value: 'services_amount',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization_billing.billing_history.total'),
            sortable: true,
            value: 'total',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('articles.table.actions'),
            sortable: false,
            value: 'actions',
            class: 'header_color text-no-wrap'
          }
        ]
      },
      fileType: null,
      module: 'BillingHistory',
      fileTypes: [
        { name: 'XLSX', value: 'xlsx' },
        { name: 'CSV', value: 'csv' }
      ],
      filters: {
        organization_id: this.organization_id
      }
    }
  },
  async mounted() {
    this.loading = true
    await this.fetchBillingHistory()
    this.loading = false
  },
  watch: {
    'billingHistoryTable.pagination': {
      async handler() {
        await this.fetchBillingHistory()
      },
      deep: true
    },
    async organizationPlan() {
      await this.fetchBillingHistory()
    },
    async updateRequired() {
      if (this.updateRequired) {
        await this.fetchBillingHistory()
        this.setUpdateRequired(false)
      }
    }
  },
  computed: {
    ...mapState({
      organizationPlan: ({ organization: { organizationPlan } }) =>
        organizationPlan,
      billingHistory: ({ billings: { billingHistory } }) => billingHistory,
      updateRequired: ({ billings: { updateRequired } }) => updateRequired,
      isBillingDetailOpen: ({ billings: { isBillingDetailOpen } }) =>
        isBillingDetailOpen,
      billingHistoryTotal: ({ billings: { billingHistoryTotal } }) =>
        billingHistoryTotal,
      organizationCreatedAt: ({ organization: { organization } }) =>
        organization.created_at
    })
  },
  methods: {
    ...mapActions({
      getBillingHistory: 'billings/getBillingHistory',
      deleteBilling: 'billings/removeBilling',
      exportData: 'downloads/exportData'
    }),
    ...mapMutations({
      setBillingDetail: 'billings/setBillingDetail',
      setUpdateRequired: 'billings/setUpdateRequired',
      toggleDialogMessage: 'downloads/toggleDialogMessage',
      toggleBillingDetail: 'billings/toggleBillingDetail'
    }),
    async fetchBillingHistory() {
      let { sortBy, sortDesc = false } = this.billingHistoryTable.pagination
      const { page, itemsPerPage } = this.billingHistoryTable.pagination
      const { organization_id } = this
      if (Array.isArray(sortBy)) {
        ;[sortBy] = sortBy
      }
      if (Array.isArray(sortDesc)) {
        ;[sortDesc] = sortDesc
      }

      await this.getBillingHistory({
        organization_id,
        page,
        rowsPerPage: itemsPerPage,
        sortBy: sortBy || 'billing_date',
        descending: sortDesc
      })

      this.billingHistoryTable.items = this.billingHistory
      this.billingHistoryTable.total = Number(this.billingHistoryTotal)
    },
    async removeBilling(billing_id) {
      await this.deleteBilling(billing_id)
    },
    async openBillingDetails(billing) {
      this.toggleBillingDetail(true)
      this.setBillingDetail(billing)
    },
    async exportFile() {
      const isValid = await this.$validator.validateAll()
      if (isValid) {
        this.submit()
      }
    },
    async submit() {
      this.exportLoading = true
      let res = await this.exportData({
        type: this.fileType,
        module: this.module,
        filename: this.module,
        is_admin: true,
        filters: this.filters
      })
      this.exportLoading = false
      if (res.success) {
        this.close()
        this.toggleDialogMessage(true)
      }
    },
    close() {
      this.exportDialog = false
      this.exportLoading = false
      this.module = null
      this.fileType = null
      this.$validator.reset()
      this.toggleBillingDetail(false)
      this.setBillingDetail({})
    }
  }
}
</script>

<style>
.header_color {
  color: #2661ff !important;
}
</style>
